import React from 'react';
import { Box } from '@wix/design-system';
import {
  AppGallery,
  AppOverview,
  AppPageLeanHeader,
  LeanAppOverview,
  VisibilityDetector,
} from '@wix/app-market-components';
import type { getAppPageDataResponse } from '../app-page-api';
import { useQuickInfoActions } from '../app-page-sections/quick-info/quick-info-actions';
import { AppPermissionsAccordion } from './app-permissions-accordion';
import css from './lean-app-page-info.module.scss';
import {
  Route,
  useSiteContext,
  useEssentials,
  useRouterContext,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import { shouldShowPermissionsAndTerms } from '../utils/permissionsAndTerms';
import { dataHooks } from './data-hooks';
import { AppPageType } from '@wix/app-market-core-components-types';
import { appMarketDemoLinkClicked } from '@wix/bi-logger-app-market-data/v2';

type LeanAppPageInfoProps = Pick<
  getAppPageDataResponse,
  'app' | 'overview' | 'permissions' | 'quickInfo'
> & {
  showNotificationGap?: boolean;
  isSlim?: boolean;
  onShowHeader?: () => void;
  onHideHeader?: () => void;
};

export function LeanAppPageInfo({
  app,
  overview,
  quickInfo,
  onShowHeader,
  onHideHeader,
  permissions = [],
  showNotificationGap = false,
  isSlim = false,
}: LeanAppPageInfoProps) {
  const { languageCode, isPremium } = useSiteContext();
  const { i18n, experiments, biLogger } = useEssentials();
  const isSlimExperimentEnabled = Boolean(
    experiments?.enabled('spec.app-market.OneStepInstallationSlimUI'),
  );
  const isLeanSkin = !isSlim;
  const { onGalleryChange, onExpandDescriptionButtonClick } =
    useQuickInfoActions(AppPageType.ONE_STEP_SLIM);
  const { baseURL } = useSiteContext();
  const shouldShowPermissions = shouldShowPermissionsAndTerms(
    permissions ?? [],
    app.installation.isInstalled,
  );

  const appPageRoute = new Route({
    baseURL,
    path: RoutePath.APP_PAGE,
    payload: { slug: app.slug },
  });

  const { router } = useRouterContext();

  const handleLearnMoreClick = () => {
    router.navigateTo({
      path: RoutePath.APP_PAGE,
      payload: { slug: app.slug },
    });
  };
  return (
    <Box
      className={
        isSlimExperimentEnabled || isLeanSkin ? css.container : css.oldContainer
      }
      dataHook={dataHooks.leanAppPageInfo}
      maxHeight={showNotificationGap ? '350px' : '425px'}
    >
      <Box
        direction="vertical"
        gap="SP4"
        className={css.appInfo}
        dataHook={dataHooks.leanAppPageInfoAppInfo}
      >
        {isSlim && isSlimExperimentEnabled && (
          <Box dataHook={dataHooks.leanAppPageInfoLeanHeaderContainer}>
            <VisibilityDetector
              onHidden={onShowHeader}
              onVisible={onHideHeader}
            >
              <AppPageLeanHeader
                image={app.icon}
                appData={app}
                isPremiumSite={isPremium}
                languageCode={languageCode}
                i18n={i18n}
                experiments={experiments}
                isNewSlim={isSlimExperimentEnabled && isSlim}
              />
            </VisibilityDetector>
          </Box>
        )}
        {isSlimExperimentEnabled ? (
          <AppOverview
            appName={app.name}
            description={!isSlim ? undefined : overview.description}
            benefits={overview.benefits}
            onExpandToggleClick={(isExpanded: boolean) =>
              onExpandDescriptionButtonClick?.(app.id, isExpanded)
            }
            rows={shouldShowPermissions ? 8 : 0}
            collapsible={shouldShowPermissions}
            showHeading={!isSlim}
            size="small"
            onLearnMoreClick={!isSlim ? handleLearnMoreClick : undefined}
            languageCode={languageCode}
            showAdaptiveLayout={true}
          />
        ) : shouldShowPermissions ? (
          <LeanAppOverview
            appName={app.name}
            benefits={overview.benefits}
            learnMoreHref={appPageRoute.toHref()}
            onExpandToggleClick={(isExpanded: boolean) =>
              onExpandDescriptionButtonClick?.(app.id, isExpanded)
            }
            languageCode={languageCode}
          />
        ) : (
          <AppOverview
            appName={app.name}
            description={overview.description}
            benefits={overview.benefits}
            onClickDemoUrl={() => {
              biLogger.report(
                appMarketDemoLinkClicked({
                  app_id: app.id,
                  demo_link: overview.demoUrl,
                  app_page_type: AppPageType.ONE_STEP_SLIM,
                }),
              );
            }}
            collapsible={false}
            showHeading={true}
            size="large"
            rows={20}
            showAdaptiveLayout={false}
          />
        )}

        {shouldShowPermissions && (
          <AppPermissionsAccordion
            appId={app.id}
            appName={app.name}
            permissions={permissions}
            appPageType={AppPageType.ONE_STEP_SLIM}
          />
        )}
      </Box>

      <div
        className={css.appGallery}
        data-hook={dataHooks.leanAppPageInfoAppGallery}
      >
        <AppGallery
          media={quickInfo.media}
          onGalleryChange={(oldIndex, newIndex) =>
            onGalleryChange(oldIndex, newIndex, app.id)
          }
        />
      </div>
    </Box>
  );
}
